import { AfterContentInit, Component, Inject, forwardRef } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialog, MatDialogConfig, MatDialogRef } from "@angular/material/dialog";
import { take } from "rxjs";
import { ActionDefinition, ActionStatus, ActionTopic, PagedList, Thing, ThingActionOverride, WorkOrder } from "../../model";
import { AppService } from "../../service/app.service";
import { AuthenticationService } from "../../service/authentication.service";
import { ThingActionOverrideService } from "../../service/thing-action-override.service";
import { UserActionStatusService } from "../../service/user-action-status.service";
import { CustomTableColumn, CustomTableService } from "../../shared/custom-table";
import { ListWidgetV2Component } from "../../shared/list-widget-v2/list-widget-v2.components";
import { ErrorUtility } from "../../utility/error-utility";
import { ActionListWidgetV2Service } from "./action-list-widget-v2.service";
import { CreateUncodedActionDialogComponent } from "./create-uncoded-action-dialog.component";


@Component({
    selector: 'add-action-dialog',
    template: require('./add-action-dialog.component.html'),
    providers: [ActionListWidgetV2Service]
})
export class AddActionDialogComponent extends ListWidgetV2Component<ActionDefinition> implements AfterContentInit {

    topics: ActionTopic[];
    actionDefinitions: ActionDefinition[];
    displayedColumns: CustomTableColumn[] = [
        CustomTableService.newIconColumn('disabledTableRow', '', 'disabledTableRow', {
            "_default": {
                isFontAwesome: false,
                customIconClass: 'material-symbols-outlined filled-icon',
                customIconHtml: 'arrow_circle_down',
                iconStyle: { 'color': '#9E9E9E' }
            }
        })
            .withVisiblePath('disabledTableRow')
            .withStyle({ '_any': { 'padding-top': '4px', 'width': '1%' } })
    ];
    uncodedDialogOnly: boolean;

    private thing: Thing;
    private actionListWidgetV2Service: ActionListWidgetV2Service;
    private isActive: boolean;
    private firstSearch: boolean = true;
    private defaultDisplayedColumns: CustomTableColumn[] = [
        CustomTableService.newSimpleColumn('label', 'actionProperty', 'label').withSortField('label'),
        CustomTableService.newSimpleColumn('topic', 'actionDefinitionTopicProperty', 'topic').withSortField('topic'),
        CustomTableService.newSimpleColumn('type', 'actionDefinitionTypeProperty', 'type.label').withSortField('type.label'),
        CustomTableService.newSimpleColumn('workTime', 'workTimeInMinutesProperty', 'workTime').withSortField('workTime')
    ];
    private workOrder: WorkOrder;

    constructor(
        @Inject(forwardRef(() => MatDialogRef)) public dialogRef: MatDialogRef<AddActionDialogComponent>,
        @Inject(MAT_DIALOG_DATA) data,
        @Inject(forwardRef(() => AuthenticationService)) authenticationService: AuthenticationService,
        @Inject(forwardRef(() => AppService)) appService: AppService,
        @Inject(forwardRef(() => MatDialog)) dialog: MatDialog,
        @Inject(forwardRef(() => UserActionStatusService)) private userActionStatusService: UserActionStatusService,
        @Inject(forwardRef(() => ThingActionOverrideService)) private thingActionOverrideService: ThingActionOverrideService
    ) {
        super(appService, authenticationService, null, null, dialog, null, null);
        this.thing = data.thing;
        this.isActive = data.isActive;
        this.actionListWidgetV2Service = data.actionListWidgetV2Service;
        this.workOrder = data.workOrder;
        this.displayedColumns = data.displayedColumns?.length ? this.displayedColumns.concat(data.displayedColumns) : this.displayedColumns.concat(this.defaultDisplayedColumns);
        this.pageSize = 10;
    }

    ngAfterContentInit(): void {
        this.sort = ['label', 'asc'];
        this.getActionDefinitionList();
    }

    getActionDefinitionList(): void {
        this.loaded = false;
        let promises = [];
        promises.push(this.actionListWidgetV2Service.getActionDefinitionPagedList(this.pageIndex, this.pageSize, this.sort, this.advancedSearchBody, this.topics));
        promises.push(this.thingActionOverrideService.getByThingId(this.thing.id));
        Promise.all(promises).then(results => {
            let pagedList: PagedList<ActionDefinition> = results[0];
            const thingActionOverrides: ThingActionOverride[] = results[1];
            this.actionDefinitions = pagedList.content;
            this.error = null;
            if (this.isActive) {
                this.actionListWidgetV2Service.getRecursivelyAllStatuses().then(allStatuses => {
                    this.enrichElementList(pagedList, allStatuses, thingActionOverrides);
                    this.updateElementList(pagedList);
                    this.checkOpenCreateUncodedDialog();
                }).catch(err => this.error = ErrorUtility.getMessage(err));
            } else {
                this.updateElementList(pagedList);
                this.checkOpenCreateUncodedDialog();
            }
        }).catch(err => this.error = ErrorUtility.getMessage(err));
    }

    private enrichElementList(pagedList: PagedList<ActionDefinition>, allStatuses: ActionStatus[], thingActionOverrides: ThingActionOverride[]): void {
        pagedList.content.forEach(a => {
            a['disabledTableRow'] = allStatuses.some(s => s.actionDefinitionId == a.id);
            this.handleThingActionOverrides(a, thingActionOverrides);
        });
    }

    private handleThingActionOverrides(actionDefinition: ActionDefinition, thingActionOverrides: ThingActionOverride[]): void {
        const overrides = thingActionOverrides.filter(o => o.actionDefinitionId == actionDefinition.id && !o.actionPropertyDefinitionId);
        overrides.forEach(override => {
            if (override.sparePartReferences) {
                actionDefinition.sparePartReferences = override.sparePartReferences;
            } else if (override.available == false) {
                actionDefinition['disabledTableRow'] = true;
            } else {    // worktime
                actionDefinition.workTime = override.workTime;
            }
        });
    }

    private checkOpenCreateUncodedDialog(): void {
        if (this.firstSearch) {
            this.firstSearch = false;
            if (!this.actionDefinitions?.length) {
                this.uncodedDialogOnly = true;
                this.dialogRef.addPanelClass('d-none'); // hide panel
                this.openCreateUncodedDialog();
            }
        }
    }

    refreshList(data?: { pageIndex: number, pageSize: number, advancedSearchBody: any, sort: string[] }): void {
        this.loaded = false;
        if (data) {
            this.pageIndex = data.pageIndex;
            this.pageSize = data.pageSize;
            this.advancedSearchBody = data.advancedSearchBody;
            this.sort = data.sort;
        }
        this.getActionDefinitionList();
    }

    protected goToDetail(element: ActionDefinition): void {
        let body: ActionStatus = {} as ActionStatus;
        body.thingId = this.thing.id;
        body.actionDefinitionId = element.id;
        if (this.isActive && this.workOrder) {
            body.workOrderId = this.workOrder.id;
        }
        this.userActionStatusService.createActionStatus(body, !this.isActive).then(() => {
            this.dialogRef.close(true);
        }).catch(err => this.error = ErrorUtility.getMessage(err));
    }

    protected subscribeToExportServices(): void {
        // do nothing
    }

    openCreateUncodedDialog(): void {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.minWidth = '25%';
        dialogConfig.maxWidth = '428px';
        dialogConfig.panelClass = "create-uncoded-action-dialog";
        dialogConfig.autoFocus = false;
        dialogConfig.data = {
            thing: this.thing,
            isActive: this.isActive,
            workOrder: this.workOrder
        }
        this.dialog.open(CreateUncodedActionDialogComponent, dialogConfig).afterClosed().pipe(take(1)).subscribe(refresh => {
            if (refresh) {
                this.dialogRef.close(true);
            } else if (this.uncodedDialogOnly) {
                this.dialogRef.close(false);
            }
        });
    }
}